import Card from '../../components/ui/Card';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { bebauungsplaeneApi } from '../../api/bebauungsplaene';
import { GEBIETSART_LABELS } from '../../types/bebauungsplan';

export default function Bebauungsplaene() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [gebietsartFilter, setGebietsartFilter] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['bebauungsplaene', { searchText, gebietsartFilter }],
    queryFn: () =>
      bebauungsplaeneApi.list({
        search_text: searchText || undefined,
        gebietsart: gebietsartFilter || undefined,
        per_page: 200,
      }),
  });

  const createDraft = useMutation({
    mutationFn: () => bebauungsplaeneApi.create(),
    onSuccess: (bp) => {
      queryClient.invalidateQueries({ queryKey: ['bebauungsplaene'] });
      navigate(`/bebauungsplaene/${bp.id}`);
    },
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">Bebauungspläne</h1>
          <button
            onClick={() => createDraft.mutate()}
            disabled={createDraft.isPending}
            className="btn btn-primary font-semibold"
          >
            {createDraft.isPending ? 'Wird angelegt…' : 'Neuer B-Plan'}
          </button>
        </div>

        <Card className="p-4 mb-4 flex flex-wrap gap-3">
          <input
            type="text"
            placeholder="Suche nach Name oder Plan-Nr…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="flex-1 min-w-[240px] border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <select
            value={gebietsartFilter}
            onChange={(e) => setGebietsartFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Alle Gebietsarten</option>
            {Object.entries(GEBIETSART_LABELS).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </Card>

        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Plan-Nr.</th>
                <th className="px-4 py-3">Gebietsart</th>
                <th className="px-4 py-3">Rechtskraft</th>
                <th className="px-4 py-3">Quelle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Lade…</td></tr>
              )}
              {!isLoading && (!data || !data.items || data.items.length === 0) && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Keine Bebauungspläne hinterlegt.</td></tr>
              )}
              {data?.items?.map((bp) => (
                <tr key={bp.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/bebauungsplaene/${bp.id}`)}>
                  <td className="px-4 py-3 text-gray-900 font-medium">
                    <Link to={`/bebauungsplaene/${bp.id}`} className="hover:underline">{bp.name}</Link>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{bp.plan_nr || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${
                      bp.gebietsart === 'WR'
                        ? 'bg-red-50 text-red-700 ring-red-200'
                        : 'bg-gray-100 text-gray-700 ring-gray-200'
                    }`}>
                      {GEBIETSART_LABELS[bp.gebietsart] ?? bp.gebietsart}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{bp.rechtskraft_datum || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{bp.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </main>
    </div>
  );
}
